/** ******************************************************* {COPYRIGHT-TOP} ***
 * Licensed Materials - Property of IBM
 *
 * (C) Copyright IBM Corporation 2016, 2025
 *
 * All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 ********************************************************** {COPYRIGHT-END} * */

import React, { useState } from 'react';
import classNames from 'classnames';
import SplitPane from '@soof-golan/react-split-pane';
import { useTranslation } from 'react-i18next';
import ExplorerHeader from './ExplorerHeader';
import Menu from './Menu';
import withActionHandler from '../utils/withActionHandler';
import { useConfig } from '../contexts/config';

const renderSVGRefs = () => (
  <svg key="svgs">
    <symbol viewBox="0 0 32 32" fillRule="evenodd" id="copy">
      <path d="M28,10V28H10V10H28m0-2H10a2,2,0,0,0-2,2V28a2,2,0,0,0,2,2H28a2,2,0,0,0,2-2V10a2,2,0,0,0-2-2Z" transform="translate(0)" />
      <path d="M4,18H2V4A2,2,0,0,1,4,2H18V4H4Z" transform="translate(0)" />
    </symbol>
    <symbol viewBox="0 0 32 32" fillRule="evenodd" id="download">
      <polygon points="26 15 24.59 13.59 17 21.17 17 2 15 2 15 21.17 7.41 13.59 6 15 16 25 26 15" />
      <path d="M26,24v4H6V24H4v4H4a2,2,0,0,0,2,2H26a2,2,0,0,0,2-2h0V24Z" />
    </symbol>
  </svg>
);

function ExplorerBody({ splitView, children }) {
  const { t } = useTranslation();
  const config = useConfig();
  const [isMenuCollapsed, updateIsMenuCollapsed] = useState(true);
  const [selectedOperation, setSelectedOperation] = useState(null);

  const toggleMenu = withActionHandler(() => {
    updateIsMenuCollapsed(!isMenuCollapsed);
  });

  const renderMobileButton = () => (
    <button
      key="expandMenuButton"
      type="button"
      tabIndex="0"
      className={classNames('expMenuCollapseButton', { expMenuShow: !isMenuCollapsed }, { headerActive: config.options.headerActive })}
      onClick={toggleMenu}
      aria-label={t('menu_button')}
      data-testid="mobile-menu-button"
    >
      <div className="hamburger-icon">
        <span className="line" />
        <span className="line" />
        <span className="line" />
      </div>
      {selectedOperation && <div className="selected-operation-wrapper truncate">{selectedOperation}</div>}
    </button>
  );

  let headerPane = null;
  if (config.options.headerActive) {
    headerPane = <ExplorerHeader />;
  }

  const defaultWidth = typeof window !== 'undefined' ? window.innerWidth / 4 : 360;
  const maxWidth = typeof window !== 'undefined' ? window.outerWidth * 0.9 : 450;
  return (
    <section className="apiconnect-explorer" aria-label="explorer-root">
      {renderSVGRefs()}
      {renderMobileButton()}
      {splitView && (
        <div data-testid="split-view">
          {headerPane}
          <SplitPane
              key="splitPane"
              split="vertical"
              className={classNames({ showResponsiveMenu: !isMenuCollapsed }, { 'header-active': config.options.headerActive })}
              minSize={10}
              defaultSize={defaultWidth}
              maxSize={maxWidth}
            >
              <Menu onSelectedOperationChange={setSelectedOperation} />
              <section id="main-explorer-content" aria-label="Main Content" data-testid="explorer-body" className="stretch" tabIndex="-1">
                {children}
              </section>
            </SplitPane>
        </div>
      )}
      {!splitView && (
        <div>
          {headerPane}
          <section id="main-explorer-content" aria-label="Main Content" tabIndex="-1">
            {children}
          </section>
        </div>
      )}
    </section>
  );
}

export default ExplorerBody;
